import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { convertMoneyToMinorUnits } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 * @property {HTMLElement} [originalPriceLine]
 * @property {HTMLElement} [currentPriceLine]
 * @property {HTMLElement} [originalPrice]
 * @property {HTMLElement} [currentPrice]
 * @property {HTMLElement} [discountRate]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {MutationObserver | null} */
  #priceObserver = null;
  /** @type {HTMLElement | null} */
  #closestSection = null;
  #discountUpdateRaf = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#closestSection = this.closest('.shopify-section, dialog');
    this.#closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);

    this.#observePriceChanges();
    this.#scheduleDiscountUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.#closestSection = null;
    this.#priceObserver?.disconnect();
    this.#priceObserver = null;
    if (this.#discountUpdateRaf) {
      cancelAnimationFrame(this.#discountUpdateRaf);
      this.#discountUpdateRaf = 0;
    }
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    this.updatedCallback();
    this.#scheduleDiscountUpdate();
  };

  #observePriceChanges() {
    this.#priceObserver?.disconnect();
    this.#priceObserver = new MutationObserver((mutations) => {
      if (!mutations.some((mutation) => this.#mutationTouchesPrice(mutation))) return;
      this.#scheduleDiscountUpdate();
    });

    this.#priceObserver.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });
  }

  /**
   * Returns true when a mutation touches the price or compare-at price nodes.
   * @param {MutationRecord} mutation
   * @returns {boolean}
   */
  #mutationTouchesPrice(mutation) {
    if (mutation.type === 'attributes' || mutation.type === 'characterData') {
      return this.#nodeTouchesPrice(mutation.target);
    }

    return (
      this.#nodeTouchesPrice(mutation.target) ||
      [...mutation.addedNodes, ...mutation.removedNodes].some((node) => this.#nodeContainsPrice(node))
    );
  }

  /**
   * Returns true when a node is the price element or inside one.
   * @param {Node} node
   * @returns {boolean}
   */
  #nodeTouchesPrice(node) {
    const element = node instanceof Element ? node : node.parentElement;
    if (!element) return false;

    const priceSelector =
      '.product-card-price__line--current .price, .product-card-price__line--current .price-discount-rate, .product-card-price__line--original .compare-at-price';

    return Boolean(element.matches(priceSelector) || element.querySelector(priceSelector));
  }

  /**
   * Returns true when a node contains one of the price elements.
   * @param {Node} node
   * @returns {boolean}
   */
  #nodeContainsPrice(node) {
    if (!(node instanceof Element)) return false;

    const priceSelector =
      '.product-card-price__line--current .price, .product-card-price__line--current .price-discount-rate, .product-card-price__line--original .compare-at-price';

    return Boolean(node.matches(priceSelector) || node.querySelector(priceSelector));
  }

  #scheduleDiscountUpdate() {
    if (this.#discountUpdateRaf) return;

    this.#discountUpdateRaf = requestAnimationFrame(() => {
      this.#discountUpdateRaf = 0;
      this.#updateDiscountBadge();
    });
  }

  #updateDiscountBadge() {
    const currentLine =
      this.refs.currentPriceLine instanceof HTMLElement
        ? this.refs.currentPriceLine
        : this.querySelector('.product-card-price__line--current, [ref="currentPriceLine"]');
    const originalLine =
      this.refs.originalPriceLine instanceof HTMLElement
        ? this.refs.originalPriceLine
        : this.querySelector('.product-card-price__line--original, [ref="originalPriceLine"]');
    const currentPrice = this.refs.currentPrice instanceof HTMLElement ? this.refs.currentPrice : currentLine?.querySelector('.price');
    const originalPrice =
      this.refs.originalPrice instanceof HTMLElement ? this.refs.originalPrice : originalLine?.querySelector('.compare-at-price');

    if (!(currentLine instanceof HTMLElement) || !(originalLine instanceof HTMLElement)) return;
    if (!(currentPrice instanceof HTMLElement) || !(originalPrice instanceof HTMLElement)) {
      this.#setOriginalLineVisibility(originalLine, false);
      const badge = this.refs.discountRate instanceof HTMLElement ? this.refs.discountRate : null;
      if (badge) {
        this.#setDiscountBadge(badge, null);
      }
      return;
    }

    const originalValue = this.#readDisplayedMoney(originalPrice);
    const currentValue = this.#readDisplayedMoney(currentPrice);
    const shouldShowOriginal =
      originalValue != null && currentValue != null && originalValue > 0 && currentValue < originalValue;

    this.#setOriginalLineVisibility(originalLine, shouldShowOriginal);

    const isProductCard = currentLine.classList.contains('product-card-price__line--current');
    const badge = isProductCard
      ? this.#getDiscountBadge(currentLine, currentPrice)
      : this.refs.discountRate instanceof HTMLElement
        ? this.refs.discountRate
        : null;

    if (!badge) return;

    if (!shouldShowOriginal || originalValue == null || currentValue == null) {
      this.#setDiscountBadge(badge, null);
      return;
    }

    const discountRate = Math.round(((originalValue - currentValue) / originalValue) * 100);
    this.#setDiscountBadge(badge, discountRate);
  }

  /**
   * Ensures the discount badge exists in the product card price line.
   * @param {HTMLElement} currentLine
   * @param {HTMLElement} currentPrice
   * @returns {HTMLElement | null}
   */
  #getDiscountBadge(currentLine, currentPrice) {
    let badge = currentLine.querySelector('.price-discount-rate');
    if (!(badge instanceof HTMLElement)) {
      badge = document.createElement('span');
      badge.className = 'price-discount-rate';
      badge.setAttribute('ref', 'discountRate');
      badge.hidden = true;
    }

    currentPrice.insertAdjacentElement('afterend', badge);
    return badge;
  }

  /**
   * Updates the discount badge text and visibility.
   * @param {HTMLElement} badge
   * @param {number | null} discountRate
   */
  #setDiscountBadge(badge, discountRate) {
    if (discountRate == null || discountRate <= 0) {
      badge.textContent = '';
      if (!badge.hidden) {
        badge.hidden = true;
      }
      return;
    }

    const nextText = `${discountRate}% OFF`;
    if (badge.textContent !== nextText) {
      badge.textContent = nextText;
    }

    if (badge.hidden) {
      badge.hidden = false;
    }
  }

  /**
   * Reads the displayed money text from a price element and converts it to minor units.
   * @param {string | HTMLElement} target
   * @returns {number | null}
   */
  #readDisplayedMoney(target) {
    const priceElement = typeof target === 'string' ? this.querySelector(target) : target;
    if (!(priceElement instanceof HTMLElement)) return null;

    const clone = priceElement.cloneNode(true);
    if (!(clone instanceof HTMLElement)) return null;

    clone.querySelectorAll('.wpd-ext-compare-at-price, .price-discount-rate, s, [hidden]').forEach((node) => node.remove());

    const rawText = clone.textContent?.replace(/\s+/g, ' ').trim();
    if (!rawText) return null;

    const currencyCode = this.dataset.currencyCode || 'USD';
    const amount = convertMoneyToMinorUnits(rawText, currencyCode);
    return amount;
  }

  /**
   * Shows or hides the original price line.
   * @param {HTMLElement} originalLine
   * @param {boolean} shouldShow
   */
  #setOriginalLineVisibility(originalLine, shouldShow) {
    const shouldHide = !shouldShow;

    if (originalLine.hidden !== shouldHide) {
      originalLine.hidden = shouldHide;
    }

    originalLine.classList.toggle('product-card-price__line--hidden', shouldHide);
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
